// src/App.js
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { ToastProvider } from './context/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import CommissionerRoute from './components/CommissionerRoute';
import SettingsRedirect from './components/SettingsRedirect';
import ErrorBoundary, { PageErrorBoundary } from './components/ErrorBoundary';

// Lazy-loaded pages — each becomes its own JS chunk
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const GettingStarted = lazy(() => import('./pages/GettingStarted'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CreateLeague = lazy(() => import('./pages/CreateLeague'));
const LeagueDashboard = lazy(() => import('./pages/LeagueDashboard'));
const LeagueSettingsPage = lazy(() => import('./pages/LeagueSettingsPage'));
const TournamentList = lazy(() => import('./pages/tournaments/TournamentList'));
const CreateTournament = lazy(() => import('./pages/tournaments/CreateTournament'));
const TournamentDashboard = lazy(() => import('./pages/tournaments/TournamentDashboard'));
const TournamentPlayerManagement = lazy(() => import('./pages/tournaments/TournamentPlayerManagement'));
const TournamentTimer = lazy(() => import('./pages/tournaments/TournamentTimer'));
const TournamentViewer = lazy(() => import('./pages/tournaments/TournamentViewer'));
const TournamentResults = lazy(() => import('./pages/tournaments/TournamentResults'));
const GameSchedulingSettings = lazy(() => import('./pages/GameSchedulingSettings'));
const ScheduledGames = lazy(() => import('./pages/ScheduledGames'));
const GameDetails = lazy(() => import('./pages/GameDetails'));
const EditResults = lazy(() => import('./pages/EditResults'));
const FixGamePlayerIds = lazy(() => import('./pages/tools/FixGamePlayerIds'));
const GameViewer = lazy(() => import('./pages/GameViewer'));
const GameTimerView = lazy(() => import('./pages/GameTimerView'));
const GameSettings = lazy(() => import('./pages/GameSettings'));
const TopEarners = lazy(() => import('./components/TopEarners'));
const JoinLeague = lazy(() => import('./pages/JoinLeague'));
const PaymentTracking = lazy(() => import('./pages/PaymentTracking'));
const SubscriptionSettings = lazy(() => import('./pages/SubscriptionSettings'));
const AdminTools = lazy(() => import('./pages/AdminTools'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const EmailPreferencesSettings = lazy(() => import('./pages/EmailPreferencesSettings'));
const NotFound = lazy(() => import('./pages/NotFound'));

const PageLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f8faf8' }}>
    <div style={{ textAlign: 'center', color: '#1a472a' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>♠</div>
      <div style={{ fontSize: '0.9rem', color: '#888' }}>Loading…</div>
    </div>
  </div>
);




function InviteRedirect() {
  const { inviteCode } = useParams();
  return <Navigate to={`/join/${inviteCode}`} replace />;
}

function MembersRedirect() {
  const { leagueId } = useParams();
  return <Navigate to={`/league/${leagueId}/settings?tab=members`} replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <SubscriptionProvider>
          <ToastProvider>
            <PageErrorBoundary>
            <Suspense fallback={<PageLoader />}>
            <Routes>
          {/* Public Landing Page */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route
            path="/settings/email"
            element={
              <ProtectedRoute>
                <EmailPreferencesSettings />
              </ProtectedRoute>
            }
          />
          
          {/* Getting Started - Post-signup onboarding for new users */}
          <Route
            path="/getting-started"
            element={
              <ProtectedRoute>
                <GettingStarted />
              </ProtectedRoute>
            }
          />
          
          {/* Admin Tools - Protected utility page */}
          <Route
            path="/admin-tools"
            element={
              <ProtectedRoute>
                <AdminTools />
              </ProtectedRoute>
            }
          />
          
          {/* League Invite - Public (handles auth internally) */}
          <Route path="/join/:inviteCode" element={<JoinLeague />} />
          <Route path="/invite/:inviteCode" element={<InviteRedirect />} />
          
          {/* Protected Dashboard - main user area after login */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-league"
            element={
              <ProtectedRoute>
                <CreateLeague />
              </ProtectedRoute>
            }
          />
          
          {/* Subscription Settings */}
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <SubscriptionSettings />
              </ProtectedRoute>
            }
          />
          {/* Redirect /pricing to /subscription */}
          <Route path="/pricing" element={<Navigate to="/subscription" replace />} />
          
          {/* Standalone Tournament Routes */}
          <Route
            path="/tournaments"
            element={
              <ProtectedRoute>
                <TournamentList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tournament/create"
            element={
              <ProtectedRoute>
                <CreateTournament />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tournament/:tournamentId"
            element={
              <ProtectedRoute>
                <TournamentDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tournament/:tournamentId/players"
            element={
              <ProtectedRoute>
                <TournamentPlayerManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tournament/:tournamentId/timer"
            element={
              <ProtectedRoute>
                <TournamentTimer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tournament/:tournamentId/view"
            element={
              <ProtectedRoute>
                <TournamentViewer />
              </ProtectedRoute>
            }
          />
          {/* Public tournament viewer - no authentication required */}
          <Route
            path="/watch/:tournamentId"
            element={<TournamentViewer />}
          />
          <Route
            path="/tournament/:tournamentId/results"
            element={
              <ProtectedRoute>
                <TournamentResults />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/league/:leagueId/dashboard"
            element={
              <ProtectedRoute>
                <LeagueDashboard />
              </ProtectedRoute>
            }
          />
          {/* NEW: Consolidated League Settings Page - Commissioner Only */}
          <Route
            path="/league/:leagueId/settings"
            element={
              <ProtectedRoute>
                <CommissionerRoute>
                  <LeagueSettingsPage />
                </CommissionerRoute>
              </ProtectedRoute>
            }
          />
          
          {/* tournament-settings → unified settings page */}
          <Route
            path="/league/:leagueId/tournament-settings"
            element={
              <ProtectedRoute>
                <CommissionerRoute>
                  <SettingsRedirect to="settings" tab="blinds" />
                </CommissionerRoute>
              </ProtectedRoute>
            }
          />
          
          {/* Legacy Settings Routes - Redirects for backwards compatibility */}
          {/* Old settings menu redirects to new consolidated page */}
          <Route
            path="/league/:leagueId/settings-legacy"
            element={
              <ProtectedRoute>
                <CommissionerRoute>
                  <SettingsRedirect to="settings" />
                </CommissionerRoute>
              </ProtectedRoute>
            }
          />
          {/* League settings redirects */}
          <Route
            path="/league/:leagueId/settings/players"
            element={
              <ProtectedRoute>
                <CommissionerRoute>
                  <SettingsRedirect to="settings" tab="members" />
                </CommissionerRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/league/:leagueId/settings/scoring"
            element={
              <ProtectedRoute>
                <CommissionerRoute>
                  <SettingsRedirect to="settings" tab="scoring" />
                </CommissionerRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/league/:leagueId/settings/payouts"
            element={
              <ProtectedRoute>
                <CommissionerRoute>
                  <SettingsRedirect to="settings" tab="payouts" />
                </CommissionerRoute>
              </ProtectedRoute>
            }
          />
          {/* Schedule page is kept as full page - Commissioner Only */}
          <Route
            path="/league/:leagueId/settings/schedule"
            element={
              <ProtectedRoute>
                <CommissionerRoute>
                  <GameSchedulingSettings />
                </CommissionerRoute>
              </ProtectedRoute>
            }
          />
          {/* Legacy tournament-settings tab redirects → unified settings */}
          <Route path="/league/:leagueId/settings/blinds"
            element={<ProtectedRoute><CommissionerRoute><SettingsRedirect to="settings" tab="blinds" /></CommissionerRoute></ProtectedRoute>} />
          <Route path="/league/:leagueId/settings/chips"
            element={<ProtectedRoute><CommissionerRoute><SettingsRedirect to="settings" tab="chips" /></CommissionerRoute></ProtectedRoute>} />
          <Route path="/league/:leagueId/settings/live-game"
            element={<ProtectedRoute><CommissionerRoute><SettingsRedirect to="settings" tab="structure" /></CommissionerRoute></ProtectedRoute>} />
          <Route path="/league/:leagueId/settings/notifications"
            element={<ProtectedRoute><CommissionerRoute><SettingsRedirect to="settings" tab="sounds" /></CommissionerRoute></ProtectedRoute>} />
          
          {/* Member Management - redirect to settings tab */}
          <Route
            path="/league/:leagueId/settings/members"
            element={<MembersRedirect />}
          />
          
          {/* Payment Tracking - Commissioner Only */}
          <Route
            path="/league/:leagueId/payments"
            element={
              <ProtectedRoute>
                <CommissionerRoute>
                  <PaymentTracking />
                </CommissionerRoute>
              </ProtectedRoute>
            }
          />
          
          {/* Game routes */}
          <Route
            path="/league/:leagueId/games"
            element={
              <ProtectedRoute>
                <ScheduledGames />
              </ProtectedRoute>
            }
          />
          <Route
            path="/league/:leagueId/tournaments"
            element={
              <ProtectedRoute>
                <ScheduledGames />
              </ProtectedRoute>
            }
          />
          <Route
            path="/league/:leagueId/scheduled"
            element={
              <ProtectedRoute>
                <ScheduledGames />
              </ProtectedRoute>
            }
          />
<Route
  path="/league/:leagueId/game/:gameId"
  element={
    <ProtectedRoute>
      <GameDetails />
    </ProtectedRoute>
  }
  />
 <Route
  path="/league/:leagueId/top-earners"
  element={
  <ProtectedRoute>
      <TopEarners />
  </ProtectedRoute>
  }
  />
 {/* Edit Results - Commissioner Only */}
 <Route
    path="/league/:leagueId/game/:gameId/edit-results"
    element={
      <ProtectedRoute>
        <CommissionerRoute>
          <EditResults />
        </CommissionerRoute>
      </ProtectedRoute>
    }
  />
  <Route
    path="/league/:leagueId/game/:gameId/results"
    element={
      <ProtectedRoute>
        <CommissionerRoute>
          <EditResults />
        </CommissionerRoute>
      </ProtectedRoute>
    }
  />
 <Route
   path="/fix-game-ids"
   element={
     <ProtectedRoute>
       <FixGamePlayerIds />
     </ProtectedRoute>
   }
 />
 <Route
   path="/league/:leagueId/game/:gameId/view"
   element={
     <ProtectedRoute>
       <GameViewer />
     </ProtectedRoute>
   }
 />
 <Route
   path="/league/:leagueId/game/:gameId/timer"
   element={
     <ProtectedRoute>
       <GameTimerView />
     </ProtectedRoute>
   }
 />
{/* Game Details - Player-facing view with RSVP */}
<Route
  path="/league/:leagueId/game/:gameId/details"
  element={
    <ProtectedRoute>
      <GameDetails />
    </ProtectedRoute>
  }
/>
{/* Game Settings - Commissioner Only */}
<Route
path="/league/:leagueId/game/:gameId/settings"
element={
<ProtectedRoute>
    <CommissionerRoute>
      <GameSettings />
    </CommissionerRoute>
</ProtectedRoute>
}
/>





          {/* 404 catch-all */}
          <Route path="*" element={<NotFound />} />

            </Routes>
            </Suspense>
            </PageErrorBoundary>
          </ToastProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
